/**
 * Knowledge / RAG admin (ADR-0009) — approved-content collections + a retrieval
 * probe. Grains ground the AI Gateway's advisory outputs; only approved grains
 * are retrievable. Admin/All-scoped (child of the Intelligence nav item).
 */
import { useState } from 'react';

import { Card } from '../components/Card';
import { DataTable, type Column } from '../components/DataTable';
import { LoadingIndicator } from '../components/LoadingIndicator';
import { PageHeader } from '../components/PageHeader';
import { useShellSlot } from '../components/shellContext';
import {
  type KnowledgeCollection,
  type RetrievedChunk,
  searchKnowledge,
  useKnowledgeCollections,
} from '../hooks/useAgentPlatform';

export function KnowledgePage() {
  useShellSlot({ breadcrumb: 'Knowledge' });
  const { items, available, isLoading, refresh } = useKnowledgeCollections();

  const [query, setQuery] = useState('');
  const [chunks, setChunks] = useState<RetrievedChunk[] | null>(null);
  const [searching, setSearching] = useState(false);

  const onSearch = () => {
    if (!query.trim()) return;
    setSearching(true);
    searchKnowledge(query)
      .then((r) => setChunks(r.chunks))
      .catch(() => setChunks([]))
      .finally(() => setSearching(false));
  };

  const collectionCols: Column<KnowledgeCollection>[] = [
    { key: 'name', header: 'Collection', render: (c) => <strong>{c.name}</strong> },
    { key: 'description', header: 'Description', render: (c) => c.description ?? '—' },
    { key: 'grains', header: 'Grains', align: 'right', render: (c) => c.grain_count ?? 0 },
  ];

  const chunkCols: Column<RetrievedChunk>[] = [
    { key: 'citation', header: 'Citation', render: (c) => <strong>{c.citation}</strong> },
    { key: 'text', header: 'Snippet', render: (c) => c.text },
    { key: 'score', header: 'Score', align: 'right', render: (c) => c.score.toFixed(2) },
  ];

  return (
    <>
      <PageHeader
        title="Knowledge / RAG"
        subtitle="Approved content that grounds advisory AI. Only approved grains are retrievable."
        actions={
          <button className="btn btn-secondary btn-sm" onClick={refresh} disabled={isLoading}>
            Refresh
          </button>
        }
      />

      {!available && (
        <p role="status" style={{ color: '#8a6d3b', fontSize: '0.86rem', marginBottom: 12 }}>
          Knowledge service unavailable — showing an empty state.
        </p>
      )}

      <Card title="Collections" padded={false} style={{ marginBottom: 16 }}>
        {isLoading && !items.length ? (
          <div style={{ padding: 24 }}>
            <LoadingIndicator text="Loading collections…" />
          </div>
        ) : (
          <DataTable
            columns={collectionCols}
            rows={items}
            rowKey={(c) => c.id}
            emptyTitle="No collections"
            emptyDescription="Ingest and approve content to ground advisory AI."
          />
        )}
      </Card>

      <Card title="Retrieval probe (approved-only)">
        <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && onSearch()}
            placeholder="e.g. how do I apply for financial assistance"
            style={{ flex: 1, padding: '8px 10px', border: '1px solid #d9d4cc', borderRadius: 8 }}
          />
          <button className="btn btn-primary btn-sm" onClick={onSearch} disabled={searching}>
            Search
          </button>
        </div>
        {searching ? (
          <LoadingIndicator text="Retrieving…" />
        ) : chunks === null ? (
          <p style={{ color: '#6b6459', fontSize: '0.86rem' }}>
            Enter a question to preview what approved content would ground an answer.
          </p>
        ) : (
          <DataTable
            columns={chunkCols}
            rows={chunks}
            rowKey={(c) => c.grain_id}
            emptyTitle="No approved matches"
            emptyDescription="No approved grain matched — the answer would not be grounded."
          />
        )}
      </Card>
    </>
  );
}
